import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionUserEvent,
} from '../../types/ServiceEvents';
import requireUser, { rosterUser } from '../../lib/requireUser';
import log from '../../lib/log';
import isValidUser from '../../lib/isValidUser';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import {
  applyAgentSessionNow,
  enqueueNonSelfAgentSessionEvent,
  shouldBufferNonSelfAgentSession,
} from '../../lib/coalesceWorkspaceEvents';

/**
 * The reject event indicates that a recipient has rejected a call.
 * @param event
 */
async function prepareAgentSessionRejected(
  event: Telecom.AgentSessionRejectedEvent,
) {
  if (!isValidUser(event.agentId)) {
    return undefined;
  }

  const user = await requireUser(event.agentId);
  return { user };
}

function commitAgentSessionRejected(
  event: Telecom.AgentSessionRejectedEvent,
  prepared: NonNullable<
    Awaited<ReturnType<typeof prepareAgentSessionRejected>>
  >,
) {
  const user = rosterUser(event.agentId, prepared.user);
  log('rtcs:agent:session_rejected:' + user?.name, event);

  user.stats = userStatsFromAgentStats(event.stats);

  ServiceUserUtils.setEventActivityStatus(user, '');

  appManager.events.emit<ServiceSessionUserEvent>(
    ServiceSessionEventType.REJECTED,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
    },
  );
}

async function handleAgentSessionRejected(
  event: Telecom.AgentSessionRejectedEvent,
) {
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_REJECTED);
  if (!shouldBufferNonSelfAgentSession(event.agentId)) {
    return applyAgentSessionNow(event.agentId, (user) => {
      commitAgentSessionRejected(event, { user });
    });
  }
  return enqueueNonSelfAgentSessionEvent(event.agentId, {
    prepare: () => prepareAgentSessionRejected(event),
    commit: (value) => commitAgentSessionRejected(event, value),
  });
}

export default handleAgentSessionRejected;
